import { isMCFCountry } from 'lib/region/countryUtils';
import { getM1DataForStore } from 'content/products/m1';
import { ProductPurchaseOption, ProductPurchaseType } from 'content/products';
import { Currency } from 'utils/Currency';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import {
  pricingFaqData_us,
  pricingFaqData_in,
  pricingFaqData_ae,
  pricingFaqData_gb_eu,
  pricingFaqData_intl,
} from 'content/pricingFaqData';
import { generateProductSchema, generateFAQSchema, generateBreadcrumbSchema } from 'lib/seo/schema';
import { extractTextFromJSX } from 'utils/extractTextFromJSX';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';

type JsonLdObject = Record<string, unknown>;

const FALLBACK_PRICE = 99;

const getBasePriceFromM1Data = (
  store: ShopifyStore | undefined,
  countryCode: string,
): number => {
  if (!store) {
    return FALLBACK_PRICE;
  }

  const m1DataForStore = getM1DataForStore(
    store,
    countryCode,
  ) as Record<
    string,
    {
      defaultProduct?: ProductPurchaseType;
      plans?: Record<
        string,
        {
          price?: number;
        }
      >;
    }
  >;

  const oneTimePurchase = m1DataForStore[ProductPurchaseOption.oneTimePurchase];

  if (!oneTimePurchase || !oneTimePurchase.plans) {
    return FALLBACK_PRICE;
  }

  const defaultKey = oneTimePurchase.defaultProduct;
  const defaultPlan =
    defaultKey && oneTimePurchase.plans[defaultKey as keyof typeof oneTimePurchase.plans];

  if (defaultPlan && typeof defaultPlan.price === 'number') {
    return defaultPlan.price;
  }

  const firstPlanWithPrice = Object.values(oneTimePurchase.plans).find(
    (plan) => plan && typeof plan.price === 'number',
  );

  if (firstPlanWithPrice && typeof firstPlanWithPrice.price === 'number') {
    return firstPlanWithPrice.price;
  }

  return FALLBACK_PRICE;
};

const getFaqDataForCountry = (countryOrLocaleCode: string) => {
  const codeUpper = countryOrLocaleCode.toUpperCase();
  const codeLower = countryOrLocaleCode.toLowerCase();

  if (codeUpper === 'US') return pricingFaqData_us;
  if (codeUpper === 'IN') return pricingFaqData_in;
  if (codeUpper === 'AE') return pricingFaqData_ae;

  const isMcf = isMCFCountry(codeLower);
  return isMcf ? pricingFaqData_gb_eu : pricingFaqData_intl;
};

/**
 * Generates Product, FAQ, and BreadcrumbList schemas for M1 pricing pages
 * based on a country/locale code. This helper is used by:
 * - /m1 (landing)
 * - /pricing/[locale]
 * - /pricing/us, /pricing/gb, /pricing/in, /pricing/ae
 */
interface M1PricingSchemaParams {
  country: string;
  store: ShopifyStore;
  currency: Currency;
  // Effective default price the page actually displays. When provided, it is
  // used verbatim so the schema price tracks the visible lead price (e.g. US
  // now defaults to the $99 membership). Falls back to getBasePriceFromM1Data
  // for callers that don't pass it.
  basePriceOverride?: number;
}

export const getM1PricingSchemas = ({
  country: countryOrLocaleCode,
  store,
  currency,
  basePriceOverride,
}: M1PricingSchemaParams): JsonLdObject[] => {
  const codeUpper = (countryOrLocaleCode || 'US').toUpperCase();
  const codeLower = codeUpper.toLowerCase();

  const basePrice =
    typeof basePriceOverride === 'number' && Number.isFinite(basePriceOverride)
      ? basePriceOverride
      : getBasePriceFromM1Data(store as ShopifyStore | undefined, codeUpper);
  const currencyString = (currency || Currency.usd).toUpperCase();

  const productSchema = generateProductSchema({
    name: codeUpper === 'US' ? 'Ultrahuman M2 CGM' : 'Ultrahuman M1 CGM',
    description:
      codeUpper === 'US'
        ? 'Ultrahuman M2 is a metabolic fitness platform that helps you improve your diet and exercise with the help of glucose biomarkers and performance coaches.'
        : 'Ultrahuman M1 is a metabolic fitness platform that helps you improve your diet and exercise with the help of glucose biomarkers and performance coaches.',
    imageUrl: getCompressedAssetUrl('/web_v2/meta-assets/m1-og-lg.png/w_1080'),
    brand: 'Ultrahuman',
    category: 'Health Monitoring',
    price: basePrice,
    currency: currencyString,
    country: codeUpper,
    availability: 'InStock',
    url: `https://www.ultrahuman.com/${codeLower}/pricing`,
    rating: {
      ratingValue: 4.8,
      reviewCount: 3500,
    },
  });

  const faqSource = getFaqDataForCountry(countryOrLocaleCode);

  const faqItems = faqSource
    .map((faq) => ({
      question: faq.title,
      answer: extractTextFromJSX(faq.description) || 'M1 FAQ answer',
    }))
    .filter((item) => item.question.trim().length > 0);

  const faqSchema = generateFAQSchema(faqItems);

  const breadcrumbSchema = generateBreadcrumbSchema([
    {
      name: 'Home',
      url: 'https://www.ultrahuman.com',
    },
    {
      name: codeUpper === 'US' ? 'M2 CGM' : 'M1 CGM',
      url: 'https://www.ultrahuman.com/m1',
    },
    {
      name: codeUpper === 'US' ? 'M2 Pricing' : 'M1 Pricing',
      url: `https://www.ultrahuman.com/${codeLower}/pricing`,
    },
  ]);

  return [productSchema, faqSchema, breadcrumbSchema];
};


